import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, Flame, HeartPulse, Headphones, Bus, Stethoscope, GraduationCap, HardHat,
  Zap, Truck, Briefcase, ShoppingBag, Landmark, MoreHorizontal,
  ArrowRight, PlayCircle, CheckCircle2, Check, X as XIcon, Lock, Cloud, Sparkles, Mail,
  Menu, X, Star, Users, TrendingUp, Plus, Facebook, Twitter, Instagram, Linkedin,
} from "lucide-react";

const NAV = [
  ["Features", "#features"], ["How It Works", "#how"], ["Pricing", "#pricing"],
  ["Who It's For", "#who"], ["FAQ", "#faq"], ["Resources", "#resources"],
];

const PROFESSIONS = [
  { icon: ShieldCheck, label: "Police Officers" }, { icon: Flame, label: "Firefighters" },
  { icon: HeartPulse, label: "Paramedics" }, { icon: Headphones, label: "911 Dispatchers" },
  { icon: Bus, label: "Transit Workers" }, { icon: Stethoscope, label: "Nurses" },
  { icon: GraduationCap, label: "Teachers" }, { icon: HardHat, label: "Construction" },
  { icon: Zap, label: "Electricians" }, { icon: Truck, label: "Truck Drivers" },
  { icon: Briefcase, label: "Office Workers" }, { icon: ShoppingBag, label: "Retail Workers" },
  { icon: Landmark, label: "Government" }, { icon: MoreHorizontal, label: "And More" },
];

const PAY_TYPES = [
  "Regular Pay", "Overtime", "Double Time", "Callback", "Call-Out", "Standby", "On-Call",
  "Shift Premium", "Night Premium", "Weekend Premium", "Holiday Pay", "Paid Duty", "Court Pay",
  "Acting Pay", "Hazard Pay", "Commission", "Tips", "Bonus", "Mileage", "Meal Allowance",
  "Vehicle Allowance", "Uniform Allowance", "Training Pay", "Preceptor Pay",
];

const STEPS = [
  { n: "1", title: "Add Your Work", items: ["Manually enter shifts and hours", "Schedule recurring shifts", "Import payroll emails automatically"] },
  { n: "2", title: "We Calculate Everything", items: ["Overtime, premiums & allowances", "Delayed pay & future raises", "Automatic paycheck projections"] },
  { n: "3", title: "Stay Ahead", items: ["Know your next paycheck", "Monthly, yearly & future projections", "Alerts so you never miss a dollar"] },
];

const COMPARE = [
  ["Future paycheck prediction", true, "×", "×"],
  ["Overtime & premium tracking", true, "Manual", "Limited"],
  ["Delayed pay tracking", true, "×", "×"],
  ["Unlimited custom pay types", true, "×", "×"],
  ["Automatic projections", true, "×", "×"],
  ["Recurring schedules", true, "×", "Limited"],
  ["Easy reports & insights", true, "×", "Limited"],
  ["All-in-one dashboard", true, "×", "Limited"],
];

const PLANS = [
  { name: "Free", price: "$0", per: "/mo", note: "Always free", cta: "Get Started Free", highlight: false,
    features: ["Up to 20 entries", "30 days of history", "Manual tracking", "Basic pay projections"] },
  { name: "Pro Monthly", price: "$7.99", per: "/mo", note: "Cancel anytime", cta: "Start Pro Monthly", highlight: true, badge: "MOST POPULAR",
    features: ["Unlimited tracking", "Email forwarding & AI extraction", "Future paycheck projections", "Powerful reports & analytics", "Priority support"] },
  { name: "Pro Annual", price: "$69.99", per: "/yr", note: "Just $5.83/month", cta: "Start Pro Annual", highlight: false, save: "SAVE 27%",
    features: ["Unlimited tracking", "Email forwarding & AI extraction", "Future paycheck projections", "Powerful reports & analytics", "Priority support"] },
];

const TRUST = [
  [Lock, "Bank-Level Encryption", "Your data is always secure"],
  [Cloud, "Secure Cloud Backup", "Your data is backed up daily"],
  [Sparkles, "AI-Powered Calculations", "Accurate & reliable results"],
  [Mail, "Automatic Email Import", "Forward payroll emails"],
  [ShieldCheck, "Available Everywhere", "Web, iPhone & Android"],
];

const TESTIMONIALS = [
  { icon: ShieldCheck, quote: "Finally, an app that shows me exactly what I'll earn. No more guessing or surprises.", who: "Toronto Police Officer" },
  { icon: HeartPulse, quote: "The projections are spot on. It helps me budget and plan for my family.", who: "Paramedic" },
  { icon: Bus, quote: "Game changer for tracking overtime, premiums and future raises.", who: "Transit Supervisor" },
];

const FAQS = [
  ["Is MyPayAlert only for police officers?", "No — MyPayAlert is built for anyone who earns a paycheck: police, firefighters, paramedics, dispatchers, transit workers, nurses, teachers, tradespeople, drivers, retail, office and government workers, and more."],
  ["How do future paycheck projections work?", "We use your pay setup (salary or hourly rate, pay frequency, premiums and deductions) plus your logged and scheduled shifts to estimate the gross and net pay of every upcoming period — including future raises and acting pay you schedule ahead."],
  ["Do I need a credit card to start?", "No. The Free plan is free forever and needs no credit card. Upgrade to Pro any time for unlimited tracking, AI import, exports and analytics."],
  ["Is my data secure?", "Yes. Your data is encrypted in transit and at rest, backed up daily, and never sold. Export or delete it any time."],
  ["Can I cancel anytime?", "Absolutely. Cancel your Pro subscription whenever you like and keep access until the end of your billing period. Your data always stays yours."],
];

function HeroMock() {
  return (
    <div className="relative w-full max-w-xl mx-auto lg:mx-0" data-testid="hero-mockup">
      <div className="rounded-2xl border border-white/10 bg-white shadow-2xl shadow-black/40 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <ShieldCheck className="h-4 w-4 text-blue-600" />
          <span className="text-[11px] font-bold text-slate-700">MyPayAlert</span>
          <span className="ml-auto text-[11px] font-semibold text-slate-500">Dashboard</span>
        </div>
        <div className="p-4 grid grid-cols-3 gap-3 bg-white">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-[9px] uppercase tracking-wide text-slate-400">Next Paycheck (Est.)</p>
            <p className="text-lg font-black text-slate-900 mt-1">$2,354.18</p>
            <p className="text-[9px] text-slate-500">Pay Day: Jul 18, 2026</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-[9px] uppercase tracking-wide text-slate-400">Total Hours</p>
            <p className="text-lg font-black text-slate-900 mt-1">80.00</p>
            <p className="text-[9px] text-slate-500">67.50 reg · 12.50 OT</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-[9px] uppercase tracking-wide text-slate-400">Total Earnings</p>
            <p className="text-lg font-black text-emerald-600 mt-1">$5,420</p>
            <p className="text-[9px] text-slate-500">This month</p>
          </div>
          <div className="col-span-1 rounded-xl bg-slate-50 border border-slate-200 p-3 flex flex-col items-center justify-center">
            <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="4" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#2563eb" strokeWidth="4" strokeDasharray="52 100" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="4" strokeDasharray="24 100" strokeDashoffset="-52" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10b981" strokeWidth="4" strokeDasharray="14 100" strokeDashoffset="-76" />
            </svg>
            <p className="text-[9px] text-slate-400 mt-1">Breakdown</p>
          </div>
          <div className="col-span-2 rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Monthly Trends</p>
            <svg viewBox="0 0 200 46" className="w-full h-11">
              <polyline fill="none" stroke="#2563eb" strokeWidth="2" points="0,36 30,30 60,32 90,20 120,24 150,14 180,16 200,8" />
            </svg>
          </div>
          <div className="col-span-3 rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1.5">
            {[["Overtime", "Jul 12", "$104.00"], ["Night Shift Premium", "Jul 10", "$96.00"], ["Regular Shift", "Jul 11", "$354.00"]].map(([a, b, c]) => (
              <div key={a} className="flex items-center justify-between text-[10px]">
                <span className="text-slate-600 font-medium">{a} <span className="text-slate-400">· {b}</span></span>
                <span className="font-bold text-slate-900">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Phone */}
      <div className="absolute -right-3 sm:-right-6 -bottom-10 w-40 sm:w-48 rounded-[1.75rem] border-4 border-slate-800 bg-[#0d1730] shadow-2xl shadow-black/50 p-3 hidden sm:block">
        <p className="text-[8px] uppercase tracking-wide text-slate-400">Paycheck Projection</p>
        <p className="text-base font-black text-white mt-0.5">$2,354.18</p>
        <p className="text-[7px] text-slate-500 mb-2">Jul 4 – Jul 17, 2026</p>
        {[["Regular Hours", "$1,620.00"], ["Overtime", "$612.50"], ["Night Shift Premium", "$192.00"], ["Holiday Premium", "$96.00"], ["Standby", "$33.60"]].map(([l, v]) => (
          <div key={l} className="flex justify-between text-[8px] py-0.5 border-b border-white/5">
            <span className="text-slate-400">{l}</span><span className="text-white font-semibold">{v}</span>
          </div>
        ))}
        <div className="flex justify-between text-[8px] pt-1.5">
          <span className="text-slate-400">Est. Deductions</span><span className="text-rose-300 font-semibold">−$360.00</span>
        </div>
        <div className="flex justify-between text-[9px] pt-1 mt-1 border-t border-white/10">
          <span className="text-emerald-300 font-bold">Est. Net Pay</span><span className="text-emerald-300 font-bold">$2,194.10</span>
        </div>
      </div>
    </div>
  );
}

const NavBtn = ({ to, children, primary, testId }) => (
  <Link to={to} data-testid={testId}
    className={primary
      ? "rounded-lg bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 text-sm font-semibold transition-colors duration-200"
      : "rounded-lg border border-white/20 hover:bg-white/10 text-white px-4 py-2 text-sm font-semibold transition-colors duration-200"}>
    {children}
  </Link>
);

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: cfg } = useQuery({ queryKey: ["public-config"], queryFn: async () => (await api.get("/public-config")).data, staleTime: 60000 });
  const c = cfg?.content || {};
  const g = (k, fallback) => (c[k] !== undefined && c[k] !== null && c[k] !== "" ? c[k] : fallback);
  const faqs = Array.isArray(c.faqs) && c.faqs.length ? c.faqs.map((f) => [f.q, f.a]) : FAQS;
  const priceFor = (p) => {
    if (!cfg) return p.price;
    if (p.name === "Pro Monthly") return `$${Number(cfg.pro_price_monthly).toFixed(2)}`;
    if (p.name === "Pro Annual") return `$${Number(cfg.pro_price_annual).toFixed(2)}`;
    return p.price;
  };
  const cell = (v) => v === true
    ? <Check className="h-4 w-4 text-blue-600 mx-auto" />
    : v === "×" ? <XIcon className="h-4 w-4 text-slate-300 mx-auto" />
    : <span className="text-xs text-slate-400">{v}</span>;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Dark hero wrapper */}
      <div className="relative bg-[#0B1121] text-white overflow-hidden">
        <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="absolute top-1/2 -left-40 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

        <header className="relative z-20">
          <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
              <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-white" /></div>
              <span className="text-xl font-black tracking-tight">MyPayAlert</span>
            </Link>
            <div className="hidden lg:flex items-center gap-7">
              {NAV.map(([label, href]) => (
                <a key={href} href={href} className="text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200">{label}</a>
              ))}
            </div>
            <div className="hidden lg:flex items-center gap-3">
              <NavBtn to="/login" testId="nav-sign-in">Sign In</NavBtn>
              <NavBtn to="/register" primary testId="nav-get-started">Get Started Free</NavBtn>
            </div>
            <button className="lg:hidden text-white" onClick={() => setMenuOpen((v) => !v)} data-testid="landing-menu-toggle" aria-label="Menu">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </nav>
          {menuOpen && (
            <div className="lg:hidden px-5 pb-5 space-y-3 border-b border-white/10" data-testid="landing-mobile-menu">
              {NAV.map(([label, href]) => <a key={href} href={href} onClick={() => setMenuOpen(false)} className="block text-sm font-medium text-slate-300">{label}</a>)}
              <div className="flex gap-3 pt-2">
                <NavBtn to="/login" testId="nav-sign-in-m">Sign In</NavBtn>
                <NavBtn to="/register" primary testId="nav-get-started-m">Get Started Free</NavBtn>
              </div>
            </div>
          )}
        </header>

        <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-10 lg:pt-16 pb-24 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="animate-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]" data-testid="hero-headline">
              {g("hero_headline_1", "Never wonder what your ")}<span className="text-blue-400">{g("hero_headline_hl", "next paycheck")}</span>{g("hero_headline_2", " will be again.")}
            </h1>
            <p className="text-slate-300 mt-6 max-w-md text-base sm:text-lg">
              {g("hero_subheadline", "Track every shift, overtime hour, premium, allowance, and future paycheck—all in one place. Know exactly what you'll earn before payday.")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register" data-testid="hero-get-started" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 font-semibold transition-colors duration-200">
                {g("hero_cta", "Get Started Free")} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#how" data-testid="hero-how-it-works" className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/10 text-white px-6 py-3 font-semibold transition-colors duration-200">
                See How It Works <PlayCircle className="h-4 w-4" />
              </a>
            </div>
            <p className="flex items-center gap-2 text-sm text-slate-400 mt-5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {g("hero_badge", "Free forever · No credit card required")}</p>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400 mt-3">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> 24+ pay types tracked</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Multi-currency support</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Built for every profession</span>
            </p>
          </div>
          <div className="lg:pl-6"><HeroMock /></div>
        </section>

        {/* Professions */}
        <section id="who" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">{g("who_title", "Built for Every Worker")}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-3">
            {PROFESSIONS.map(({ icon: Icon, label }) => (
              <div key={label} data-testid={`profession-${label}`} className="rounded-xl bg-white/5 border border-white/10 p-3 flex flex-col items-center gap-2 text-center hover:bg-white/10 transition-colors duration-200">
                <Icon className="h-6 w-6 text-blue-300" />
                <span className="text-[11px] text-slate-300 font-medium leading-tight">{label}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-300 mt-8 text-sm sm:text-base">If you earn a paycheck, <span className="text-blue-400 font-semibold">MyPayAlert</span> is for you.</p>
        </section>
      </div>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-center">{g("how_title", "How It Works")}</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map(({ n, title, items }) => (
            <div key={n} data-testid={`step-${n}`} className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-blue-600 text-white font-black flex items-center justify-center text-sm">{n}</div>
                <h3 className="font-bold text-slate-900">{title}</h3>
              </div>
              <ul className="mt-4 space-y-2">
                {items.map((it) => <li key={it} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" /> {it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Pay structure */}
      <section id="features" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-center">{g("paystructure_title", "Built for Every Pay Structure")}</h2>
          <div className="mt-10 flex flex-wrap justify-center gap-2.5">
            {PAY_TYPES.map((t) => (
              <span key={t} data-testid={`paytype-${t}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 px-3.5 py-1.5 text-xs font-medium shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> {t}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white px-3.5 py-1.5 text-xs font-semibold shadow-sm">
              <Plus className="h-3.5 w-3.5" /> Unlimited Custom Pay Types
            </span>
          </div>
        </div>
      </section>

      {/* Why choose - comparison */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">{g("why_title", "Why Choose MyPayAlert?")}</h2>
          <p className="text-slate-500 mt-4">{g("why_subtitle", "We do the work for you so you can focus on what matters most.")}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="text-left font-semibold px-4 py-3">Feature</th>
                <th className="px-3 py-3"><span className="rounded-md bg-blue-600 text-white px-2.5 py-1 text-xs font-bold">MyPayAlert</span></th>
                <th className="px-3 py-3 font-semibold">Spreadsheet</th>
                <th className="px-3 py-3 font-semibold">Payroll Portal</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-700">{row[0]}</td>
                  <td className="px-3 py-3 text-center bg-blue-50/50">{cell(row[1])}</td>
                  <td className="px-3 py-3 text-center">{cell(row[2])}</td>
                  <td className="px-3 py-3 text-center">{cell(row[3])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-center">{g("pricing_title_1", "Simple pricing. ")}<span className="text-blue-600">{g("pricing_title_hl", "More value.")}</span></h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12 items-start">
            {PLANS.map((p) => (
              <div key={p.name} data-testid={`plan-${p.name}`} className={`relative rounded-2xl bg-white p-7 ${p.highlight ? "border-2 border-blue-600 shadow-xl md:-mt-4" : "border border-slate-200 shadow-sm"}`}>
                {p.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 text-white text-[10px] font-bold px-3 py-1 uppercase tracking-wide">{p.badge}</div>}
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-slate-900">{p.name}</h3>
                  {p.save && <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2.5 py-1">{p.save}</span>}
                </div>
                <div className="mt-4 flex items-end gap-1"><span className="text-4xl font-black text-slate-900">{priceFor(p)}</span><span className="text-slate-400 font-medium mb-1">{p.per}</span></div>
                <p className="text-xs text-slate-500 mt-1">{p.note}</p>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 shrink-0" /> {f}</li>)}
                </ul>
                <Link to="/register" data-testid={`plan-cta-${p.name}`}
                  className={`mt-7 block text-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                    p.highlight ? "bg-blue-600 hover:bg-blue-500 text-white" : p.save ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "border border-slate-300 hover:border-blue-500 text-slate-700"}`}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust row */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {TRUST.map(([Icon, title, desc]) => (
            <div key={title} className="flex flex-col items-center text-center gap-2">
              <Icon className="h-7 w-7 text-blue-600" />
              <p className="font-bold text-sm text-slate-900">{title}</p>
              <p className="text-xs text-slate-500">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section id="resources" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-center mb-10">{g("testimonials_title", "Trusted by Thousands of Workers")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ icon: Icon, quote, who }, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
                <div className="flex gap-0.5 text-amber-400 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-amber-400" />)}</div>
                <p className="text-slate-700 text-sm leading-relaxed">"{quote}"</p>
                <p className="flex items-center gap-2 text-xs text-slate-500 mt-4"><Icon className="h-4 w-4 text-blue-600" /> {who}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-10">
            {[[Briefcase, "24+", "Pay types tracked"], [TrendingUp, "150+", "Currencies supported"], [ShieldCheck, "Free", "Plan available, no card needed"]].map(([Icon, big, small], i) => (
              <div key={i} className="flex items-center gap-3"><Icon className="h-6 w-6 text-blue-600" /><div><p className="font-black text-slate-900 leading-none">{big}</p><p className="text-xs text-slate-500 mt-0.5">{small}</p></div></div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-center mb-10">{g("faq_title", "Frequently asked questions")}</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
              <AccordionTrigger className="text-left text-slate-900 font-semibold hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-500">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <div className="rounded-2xl bg-[#0B1121] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">{g("cta_title", "Ready to take control of your pay?")}</h2>
            <p className="text-slate-300 mt-2">{g("cta_subtitle", "Join thousands of workers who know their worth.")}</p>
          </div>
          <div className="relative text-center">
            <Link to="/register" data-testid="final-cta" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white px-7 py-3.5 font-semibold transition-colors duration-200">{g("cta_button", "Get Started Free")} <ArrowRight className="h-4 w-4" /></Link>
            <p className="text-[11px] text-slate-400 mt-2">No credit card required</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#080d1a] text-slate-400 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-white" /></div>
              <span className="text-white font-black">MyPayAlert</span>
            </div>
            <p className="text-sm max-w-xs">{g("footer_tagline", "The all-in-one platform to track your pay, shifts and future paychecks with accuracy and confidence.")}</p>
            <div className="flex gap-3 mt-4">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => <a key={i} href="#" className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors duration-200"><Icon className="h-4 w-4" /></a>)}
            </div>
          </div>
          {[
            ["Product", [["Features", "#features"], ["How It Works", "#how"], ["Pricing", "#pricing"], ["FAQ", "#faq"]]],
            ["Company", [["Contact", "/contact"], ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"]]],
            ["Support", [["Help & Support", "mailto:support@mypayalert.com"], ["Retirement Planner", "/retirement"], ["Shift Marketplace", "/marketplace"]]],
          ].map(([title, items]) => (
            <div key={title}>
              <p className="text-white font-semibold text-sm mb-3">{title}</p>
              <ul className="space-y-2 text-sm">
                {items.map(([label, href]) => (
                  <li key={label}>
                    {href.startsWith("/") ? (
                      <Link to={href} className="hover:text-white transition-colors duration-200" data-testid={`footer-link-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{label}</Link>
                    ) : (
                      <a href={href} className="hover:text-white transition-colors duration-200" data-testid={`footer-link-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <p className="text-white font-semibold text-sm mb-3">Get in touch</p>
            <p className="text-sm mb-3">Questions or feedback? We usually reply within 1–2 business days.</p>
            <a href="mailto:support@mypayalert.com" className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors" data-testid="footer-support-email"><Mail className="h-4 w-4" /> support@mypayalert.com</a>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 px-5 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-2 text-center text-xs">
          <span>© 2026 MyPayAlert. All rights reserved.</span>
          <span className="hidden sm:inline text-white/20">·</span>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-bottom-privacy">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-bottom-terms">Terms of Service</Link>
          <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-bottom-contact">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
